import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import Button from './ui/Button';

/**
 * Fullscreen photo viewer: dark backdrop, large image, tap backdrop or X to close.
 * Image clicks do not close (stopPropagation).
 * Hand-rolled chrome (no card); uses modal z-index tokens + scroll lock + Escape like <Modal>.
 */
export default function PhotoZoomModal({ open, onClose, imageUrl, alt = '' }) {
  useEffect(() => {
    if (!open || !imageUrl) return;
    const scrollY = window.scrollY;
    const original = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
    };
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    return () => {
      document.body.style.overflow = original.overflow;
      document.body.style.position = original.position;
      document.body.style.top = original.top;
      document.body.style.width = original.width;
      window.scrollTo(0, scrollY);
    };
  }, [open, imageUrl]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !imageUrl) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-modal-backdrop flex items-center justify-center bg-black/90 p-4"
      onClick={onClose}
      role="presentation"
    >
      <Button
        type="button"
        variant="icon"
        size="md"
        aria-label="Close"
        className="absolute top-4 right-4 z-modal !bg-black/50 !text-ink hover:!bg-black/70"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      >
        <X className="h-6 w-6" />
      </Button>
      <img
        src={imageUrl}
        alt={alt || 'Profile photo'}
        className="max-h-[85vh] max-w-[85vw] rounded-xl object-contain"
        onClick={(e) => e.stopPropagation()}
      />
    </div>,
    document.body,
  );
}
